import { useEffect } from 'react';
import { useSiteConfig } from '../context/SiteConfigContext';

/**
 * Injects all SEO metadata for the current route:
 *   - <title>
 *   - <meta name="description">
 *   - <meta name="keywords"> (optional)
 *   - Open Graph tags (og:*) — Facebook, WhatsApp, LinkedIn previews
 *   - Twitter Card tags
 *   - <link rel="canonical">
 *
 * Usage:
 *   useSEO('home')                            // pulls from CMS seo.home.*
 *   useSEO('product', { title, description, image, url })  // overrides
 */
function setMeta(selector, attr, value) {
  if (!value) return;
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    // Parse selector like meta[name="x"] or meta[property="og:x"]
    const m = selector.match(/^meta\[([^=]+)="([^"]+)"\]$/);
    if (m) el.setAttribute(m[1], m[2]);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
}

function setLink(rel, href) {
  if (!href) return;
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export function useSEO(routeKey, overrides = {}) {
  const { config } = useSiteConfig();
  useEffect(() => {
    const siteTitle = config['site.title'] || 'Comptoir-Online';
    const cmsTitle = config[`seo.${routeKey}.title`];
    const cmsDesc = config[`seo.${routeKey}.description`];
    const cmsImage = config[`seo.${routeKey}.image`] || config['site.og_image'];

    const finalTitle = overrides.title || cmsTitle || siteTitle;
    const finalDesc = overrides.description || cmsDesc || config['site.tagline'] || '';
    const finalImage = overrides.image || cmsImage || '';
    const finalUrl = overrides.url || (typeof window !== 'undefined' ? window.location.href : '');

    // <title>
    const displayTitle = finalTitle.length > 60
      ? finalTitle
      : `${finalTitle} — ${siteTitle}`.replace(`${siteTitle} — ${siteTitle}`, siteTitle);
    document.title = displayTitle;

    // Standard meta
    setMeta('meta[name="description"]', 'content', finalDesc);

    // Open Graph (Facebook / WhatsApp / LinkedIn)
    setMeta('meta[property="og:title"]', 'content', finalTitle);
    setMeta('meta[property="og:description"]', 'content', finalDesc);
    setMeta('meta[property="og:type"]', 'content', overrides.type || 'website');
    if (finalUrl) setMeta('meta[property="og:url"]', 'content', finalUrl);
    if (finalImage) setMeta('meta[property="og:image"]', 'content', finalImage);

    // Twitter Card
    setMeta('meta[name="twitter:card"]', 'content', finalImage ? 'summary_large_image' : 'summary');
    setMeta('meta[name="twitter:title"]', 'content', finalTitle);
    setMeta('meta[name="twitter:description"]', 'content', finalDesc);
    if (finalImage) setMeta('meta[name="twitter:image"]', 'content', finalImage);

    // Canonical URL
    if (finalUrl) setLink('canonical', finalUrl);
  }, [config, routeKey, overrides.title, overrides.description, overrides.image, overrides.url, overrides.type]);
}

/**
 * Injects a JSON-LD structured data block into <head>.
 * Removes any previous block with the same `id` before injecting (idempotent).
 */
export function useJsonLd(id, data) {
  useEffect(() => {
    if (!data) return;
    const elId = `jsonld-${id}`;
    let el = document.getElementById(elId);
    if (!el) {
      el = document.createElement('script');
      el.id = elId;
      el.type = 'application/ld+json';
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(data);
    return () => {
      const cleanup = document.getElementById(elId);
      if (cleanup) cleanup.remove();
    };
  }, [id, JSON.stringify(data)]);  // eslint-disable-line react-hooks/exhaustive-deps
}
